import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Seo from "@/components/Seo";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowLeft, CalendarDays, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/posts/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#F7F7F9] text-[#222222]" data-testid="blog-post-page">
      <Seo
        title={post ? `${post.title} | Shonta Solutions LLC` : "Owner Resources | Shonta Solutions LLC"}
        description={post ? post.excerpt : undefined}
      />
      <Header />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <Link
          to="/"
          data-testid="blog-back-link"
          className="inline-flex items-center gap-2 text-sm font-bold text-[#FF5A5F] hover:text-[#E0484D] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Shonta Solutions
        </Link>

        {notFound && (
          <div className="mt-16 text-center" data-testid="blog-not-found">
            <h1 className="text-3xl font-bold">Article not found</h1>
            <p className="mt-3 text-[#5a5a5a]">This guide may have moved. Head back to browse our resources.</p>
          </div>
        )}

        {post && (
          <article className="mt-8">
            <p className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-[#FF5A5F]">
              <CalendarDays className="w-4 h-4" />
              {new Date(post.published_at + "T00:00:00").toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              {post.title}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-[#5a5a5a] border-l-4 border-[#FF5A5F] pl-5">
              {post.excerpt}
            </p>

            <div className="mt-10 space-y-6">
              {post.blocks.map((block, i) => {
                if (block.type === "h2") {
                  return (
                    <h2 key={i} className="text-2xl font-bold tracking-tight pt-4">
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === "list") {
                  return (
                    <ul key={i} className="space-y-3 pl-1">
                      {block.items.map((item, j) => (
                        <li key={j} className="flex gap-3 text-base leading-relaxed text-[#444]">
                          <span className="mt-2.5 w-2 h-2 rounded-full bg-[#FF5A5F] shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                }
                return (
                  <p key={i} className="text-base sm:text-lg leading-relaxed text-[#444]">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-14 bg-[#222222] rounded-2xl p-8 sm:p-10 text-white" data-testid="blog-cta">
              <h2 className="text-2xl font-bold">Curious what your property could do?</h2>
              <p className="mt-3 text-white/70 leading-relaxed">
                Book a free consultation and we'll walk through your home's potential — no pressure,
                no obligation.
              </p>
              <Link
                to="/#contact"
                data-testid="blog-cta-button"
                className="mt-6 inline-flex items-center gap-2 bg-[#FF5A5F] hover:bg-[#E0484D] text-white font-bold px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                Book a Free Consultation
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}
